/**
 * Centralized React Query keys for Chat functionality
 * Follows the pattern: ['chat', scope, ...args]
 */
export const CHAT_KEYS = {
  all: ['chat-messages'] as const,
  sessions: ['chat-sessions'] as const,
  bySession: (sessionId: string | null) => ['chat-messages', sessionId] as const,
  /** Shared per-session poll for extraction-accuracy results (deduped across inline forms). */
  accuracyPoll: (sessionId: string | null) => ['chat-accuracy-poll', sessionId] as const,
};

// Stable query keys following the pattern: ['organization', orgId, ...]
export const ORGANIZATION_KEYS = {
  all: ['organization'] as const,
  byId: (orgId: string) => [...ORGANIZATION_KEYS.all, orgId] as const,
  members: (orgId: string) => [...ORGANIZATION_KEYS.all, orgId, 'members'] as const,
  stats: (orgId: string) => [...ORGANIZATION_KEYS.all, orgId, 'stats'] as const,
  logo: (orgId: string) => [...ORGANIZATION_KEYS.all, orgId, 'logo'] as const,
  outputSettings: (orgId: string) => [...ORGANIZATION_KEYS.all, orgId, 'output-settings'] as const,
  invitations: (orgId: string) => [...ORGANIZATION_KEYS.all, orgId, 'invitations'] as const,
  llmProviders: (orgId: string) => [...ORGANIZATION_KEYS.all, orgId, 'llm-providers'] as const,
};

// Stable query keys for User Profile functionality
export const USER_KEYS = {
  all: ['user'] as const,
  profile: () => [...USER_KEYS.all, 'profile'] as const,
  photo: () => [...USER_KEYS.all, 'profile', 'photo'] as const,
  tutorialStatus: () => [...USER_KEYS.all, 'tutorial-status'] as const,
  externalApiClient: () => [...USER_KEYS.all, 'external-api-client'] as const,
};

// Stripe prices (used by Pricing and EnterpriseBilling; use for cache/invalidation)
export const STRIPE_PRICES_KEYS = {
  all: ['stripe-prices'] as const,
};

// Stable query keys for subscription / billing
const SUBSCRIPTION_FOR_ACCESS_PREFIX = ['subscription', 'for-access'] as const;

export const SUBSCRIPTION_KEYS = {
  /** Prefix for all "current subscription for access" queries; use for cache invalidation. */
  forAccessPrefix: SUBSCRIPTION_FOR_ACCESS_PREFIX,
  /** Current subscription for access/billing; key includes org vs personal so only one API is called. */
  forAccess: (isOrgUser: boolean) => [...SUBSCRIPTION_FOR_ACCESS_PREFIX, isOrgUser] as const,
};

export const BILLING_ENFORCEMENT_KEYS = {
  all: ['billing-enforcement'] as const,
  /**
   * Include org id so policy cache cannot leak across org changes in the same session
   * (GET /billing-enforcement/me is org-scoped for org members).
   */
  me: (organizationId: string | null | undefined) =>
    [...BILLING_ENFORCEMENT_KEYS.all, 'me', organizationId ?? 'none'] as const,
};
